import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { clsx } from "clsx";
import type { DocHandle } from "../sync/docManager";
import { applyQuickAdd, quickAddPropDefs } from "../core/ops";
import { parseQuickAdd } from "../core/quickadd";
import { remoteRef, resolveRemote } from "../core/remote";
import { QuickAdd } from "../views/list/QuickAdd";
import { useToasts } from "../ui/toasts";
import { useComfort } from "./comfort";

/**
 * The quick-add line on every page (comfort `quickAddBar`): the same line as on the List, adding to the workspace
 * that is open. Not on the List itself, which has its own. A short message says what was added, since the new task
 * may not be in view.
 */
export function QuickAddBar({
  core,
  workspaceId,
  place,
  alsoTop = false,
  openTask,
}: {
  core: DocHandle | null;
  workspaceId: string | null;
  place: "top" | "bottom";
  /** at the bottom, also show it when it is set *On top* (a phone, whose top bar has no room) */
  alsoTop?: boolean;
  openTask(id: string): void;
}) {
  const { t } = useTranslation();
  const { quickAddBar } = useComfort();
  const { pathname } = useLocation();
  const toast = useToasts((s) => s.push);
  const here = quickAddBar === place || (alsoTop && quickAddBar === "top");
  if (!here || pathname === "/list" || !core || core.readOnly) return null;
  const add = (text: string) => {
    const remote = remoteRef(text);
    if (remote && workspaceId) {
      resolveRemote(workspaceId, remote).then(openTask, (e: Error) =>
        toast(t(`errors.${e.message}`, { defaultValue: t("remote.failed") })),
      );
      return;
    }
    const parsed = parseQuickAdd(text, { propDefs: quickAddPropDefs(core.doc), now: new Date() });
    const id = applyQuickAdd(core.doc, parsed);
    toast(t("quickAdd.added", { title: parsed.title }), {
      action: { label: t("quickAdd.open"), run: () => openTask(id) },
    });
  };
  return (
    <div
      className={clsx(
        place === "top"
          ? "min-w-0 max-w-md flex-1"
          : "shrink-0 border-t border-neutral-200 bg-[var(--surface-2)] px-3 py-1.5 mid:px-7 dark:border-neutral-800",
      )}
      data-testid={`quick-add-bar-${place}`}
    >
      <QuickAdd onAdd={add} showSyntaxHint />
    </div>
  );
}
